// 時間前市場の GC (Gate Close) カウントダウン計算。
//
// JEPX 時間前市場のコマ別 GC は「該当コマの開始 1 時間前」 が原則 (= 仕様による)。
// 受渡日 + コマ番号 (1-48) から GC 時刻を算出する純関数。
import { jstWallClockToInstant } from '~/models/dateTime'

export type CountdownParts = {
  /** GC までの残り秒。 到来済みなら 0。 */
  totalSeconds: number
  isPast: boolean
}
// 旧 hours / minutes / seconds は表示側が `~/models/gcRemainingTime` の
// formatGcRemainingTime (HH:MM:SS 整形) に一本化されて参照が無くなったため削除した
// (#4858)。 時分秒が要るなら整形側で totalSeconds から出す。

/** GC はコマ開始の何分前か (= 仕様「GC = 受渡コマ開始の 1 時間前」)。 */
const GC_LEAD_MINUTES = 60

/**
 * 受渡日とコマ番号から GC 時刻を計算する。
 * コマ 1 (00:00-00:30) の GC は受渡日前日 23:00、 コマ 2 (00:30-01:00) の GC は前日 23:30。
 *
 * コマ帯も GC も **JST の壁時計**で定義される業務概念なので、 受渡日 (= カレンダー日
 * `Date`) の暦日を JST 壁時計として絶対時刻に落とす ([[jstWallClockToInstant]])。
 * 端末ローカル時刻で組むと非 JST 端末で GC が最大 9 時間ズレる (#4765)。
 */
export const calcGcAtForKoma = (targetDate: Date, koma: number): Date => {
  // コマ開始時刻 (JST 壁時計)。 GC = その 1 時間前 (= コマ 1 / 2 は前日に跨るが、
  // 分の桁下がりは `jstWallClockToInstant` の `Date.UTC` が吸収する)。
  const startHour = Math.floor((koma - 1) / 2)
  const startMinute = (koma - 1) % 2 === 0 ? 0 : 30
  return jstWallClockToInstant(
    targetDate,
    startHour,
    startMinute - GC_LEAD_MINUTES,
  )
}

export const computeCountdown = (now: Date, gcAt: Date): CountdownParts => {
  const diffMs = gcAt.getTime() - now.getTime()
  if (diffMs <= 0) {
    return { totalSeconds: 0, isPast: true }
  }
  return { totalSeconds: Math.floor(diffMs / 1000), isPast: false }
}

// GC 境界コマ (= どのコマが GC 通過済みか) の導出は受渡日にも依存するため
// `deliveryDate.ts` が持つ ([[gcBoundarySlot]] / [[firstOpenSlot]] / [[gcLineSlot]])。
// コマ番号の加減算で境界を近似する helper (旧 `currentSlotForToday` /
// `gcSlotIndexForToday`) は、 起点が現在進行中コマで境界が 30 分手前にズレるため置かない。
//
// **時間前市場の時刻判定一式は JST 壁時計基準**で揃える (#4765)。 受渡日比較
// (`models/deliveryDate`)・開場時刻 (`models/marketOpen`)・取り下げ時刻
// (`models/withdrawAt`)・「今日」 の導出も同じ軸に載せてあり、 一部だけローカル基準に
// 残すと GC 境界コマとカウントダウン対象が最大 9 時間ぶん食い違う (= PR #4761 で
// revert した混在。 frontend の test は `vitest.config.ts` で `TZ: 'Asia/Tokyo'` 固定の
// ため、 非 JST を明示した test でしか検出できない)。
